import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import {
  bodyTooltip,
  chartTitle,
  multiSeriesLegend,
  type OptionContext,
} from './shared.ts';

export function buildRadarOption(
  config: Extract<ChartConfig, { type: 'radar' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const globalMax = Math.max(...config.series.flatMap((s) => s.values), 1);
  const niceMax = (() => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(globalMax)));
    const candidates = [1, 2, 2.5, 5, 10].map(
      (f) => Math.ceil(globalMax / (f * magnitude)) * f * magnitude
    );
    return candidates.find((c) => c >= globalMax) ?? globalMax;
  })();

  const seriesColors = [
    colors.primary,
    colors.positive,
    colors.negative,
    '#6366f1',
    '#f59e0b',
  ];

  const truncate = (s: string, n = 14) =>
    s.length > n ? s.slice(0, n - 1) + '…' : s;

  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({ trigger: 'item' }),
    legend: multiSeriesLegend(config.series.length, textColor),
    radar: {
      indicator: config.axes.map((name) => ({
        name: truncate(name),
        max: niceMax,
      })),
      axisName: { color: textColor, fontSize: 9 },
      splitLine: { lineStyle: { color: axisLineColor } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: axisLineColor } },
      center: ['50%', config.title ? '54%' : '50%'],
      radius: config.series.length > 1 ? '55%' : '58%',
    },
    series: [
      {
        type: 'radar',
        data: config.series.map((s, i) => ({
          name: s.name,
          value: s.values,
          lineStyle: {
            color: seriesColors[i % seriesColors.length],
            width: 2,
          },
          itemStyle: { color: seriesColors[i % seriesColors.length] },
          areaStyle: {
            color: seriesColors[i % seriesColors.length],
            opacity: config.series.length === 1 ? 0.18 : 0.08,
          },
        })),
      },
    ],
  };
}
